<template>
  <!-- Loading State -->
  <div class="loading-container" v-if="loading">
    <div class="spinner-wrapper">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('loading') }}</span>
      </div>
    </div>
  </div>

  <!-- Main Content - Mobile Optimized -->
  <div class="event-dashboard mobile-optimized" v-if="!loading">
    <!-- Mobile Header with Banner -->
    <div class="mobile-header-wrapper">
      <div class="mobile-header" v-if="event_details.event.banners.length">
        <carousel :items-to-show="1" :autoplay="3000" :wrap-around="true">
          <slide v-for="banner in event_details.event.banners" :key="banner.id">
            <div class="mobile-banner-slide">
              <img :src="banner.image" :alt="event_details.event.name" />
              <div class="banner-gradient"></div>
              <div class="banner-content">
                <h1 class="event-title-overlay">{{ event_details.event.name }}</h1>
              </div>
            </div>
          </slide>
          <template #addons>
            <pagination />
          </template>
        </carousel>
      </div>

      <!-- Event Info Bar when no banner -->
      <div class="event-info-bar" v-if="!event_details.event.banners.length">
        <h1 class="mobile-event-title">{{ event_details.event.name }}</h1>
      </div>
      
      <!-- Event Meta Information - Standard Design -->
      <div class="event-meta-card" v-if="event_details.event.date || event_details.event.location">
        <div class="meta-row">
          <div class="meta-icon-wrapper">
            <ion-icon name="calendar-outline"></ion-icon>
          </div>
          <div class="meta-content">
            <span class="meta-label">{{ $t('date') }}</span>
            <span class="meta-value">{{ event_details.event.date || '2025-11-16' }}</span>
          </div>
        </div>
        <div class="meta-divider"></div>
        <div class="meta-row">
          <div class="meta-icon-wrapper">
            <ion-icon name="location-outline"></ion-icon>
          </div>
          <div class="meta-content">
            <span class="meta-label">{{ $t('location') }}</span>
            <span class="meta-value">{{ event_details.event.place || 'Location' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Primary CTA - Mobile Optimized -->
    <div class="mobile-primary-cta" v-if="event_details.self_link || event_details.self_reg">
      <a :href="event_details.self_link" v-if="event_details.self_link" class="cta-link">
        <button class="mobile-primary-btn" :class="event_details.self_color">
          <div class="mobile-btn-content">
            <div class="btn-icon-wrapper">
              <ion-icon name="checkmark-circle"></ion-icon>
            </div>
            <div class="btn-text-wrapper">
              <span class="btn-main-text">{{ event_details.self_text }}</span>
            </div>
            <ion-icon name="chevron-forward" class="btn-chevron"></ion-icon>
          </div>
        </button>
      </a>
      
      <button 
        v-if="event_details.self_reg" 
        class="mobile-primary-btn" 
        :class="event_details.self_color"
        @click="addAttendanceModelShow"
      >
        <div class="mobile-btn-content">
          <div class="btn-icon-wrapper">
            <ion-icon name="person-add"></ion-icon>
          </div>
          <div class="btn-text-wrapper">
            <span class="btn-main-text">{{ event_details.self_text }}</span>
            <span class="btn-sub-text">{{ $t('register_now') }}</span>
          </div>
          <ion-icon name="chevron-forward" class="btn-chevron"></ion-icon>
        </div>
      </button>
    </div>

    <!-- Quick Stats Bar - Mobile -->
    <div class="mobile-stats-bar" v-if="event_details.stats">
      <div class="stat-item">
        <div class="stat-value">{{ event_details.stats.registered || 0 }}</div>
        <div class="stat-label">{{ $t('registered') }}</div>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item">
        <div class="stat-value">{{ event_details.stats.attended || 0 }}</div>
        <div class="stat-label">{{ $t('attended') }}</div>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item">
        <div class="stat-value">{{ event_details.stats.pending || 0 }}</div>
        <div class="stat-label">{{ $t('pending') }}</div>
      </div>
    </div>

    <!-- Quick Actions - Mobile Optimized Grid -->
    <div class="mobile-actions-section" v-if="event_details.common_buttons.length">
      <div class="section-content-wrapper">
        
        <div class="mobile-action-grid">
          <router-link 
            v-for="(button, index) in event_details.common_buttons" 
            :key="button.slug"
            :to="button.url"
            class="mobile-action-item"
          >
            <div class="action-tile">
              <div class="tile-icon" :class="button.class || getButtonColorClass(index)">
                <ion-icon :name="button.icon || getIconName(button.name)"></ion-icon>
              </div>
              <span class="tile-label">{{ button.name }}</span>
              <div class="tile-badge" v-if="button.badge">
                {{ button.badge }}
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Admin Section - Button Style -->
    <div class="mobile-admin-section" v-if="event_details.admin_buttons.length">
      <div class="section-wrapper">
        <!-- Admin Header -->
        <div class="section-header">
          <div class="admin-title-wrapper">
            <ion-icon name="shield-checkmark" class="admin-header-icon"></ion-icon>
            <h2 class="mobile-section-title">{{ $t('admin_controls') }}</h2>
          </div>
          <span class="admin-state-badge">
            {{ currentAdminLevel == 7 ? (admin_levels['campus'] || 'Campus') : admin_levels['org'] }}
          </span>
        </div>
        
        <!-- Switch Admin Level Button -->
        <button 
          v-if="canSwitchAdminLevel"
          class="admin-level-switch"
          @click="showAdminSwitchModal"
        >
          <ion-icon name="swap-horizontal"></ion-icon>
          <span>{{ $t('switch_admin_level') }}</span>
        </button>

        <!-- Admin Actions Grid - Button Style -->
        <div class="admin-actions-grid">
          <router-link 
            v-for="(button, index) in mainAdminButtons" 
            :key="button.slug || button.name"
            :to="button.url"
            class="admin-grid-item"
            :title="button.name"
          >
            <div class="admin-button-tile">
              <div class="admin-button-icon" :class="button.class || button.colorClass || getAdminColorClass(index)">
                <ion-icon :name="button.icon || button.iconName || getAdminIconName(button.name)"></ion-icon>
              </div>
              <span class="admin-button-label">{{ button.name }}</span>
              <span class="admin-button-count" v-if="button.count">{{ button.count }}</span>
            </div>
          </router-link>
        </div>
        <!-- Separate section for bus-related buttons -->
  <div v-if="busAdminButtons.length" class="admin-actions-grid bus-section">
          <router-link 
            v-for="(button, index) in busAdminButtons" 
            :key="button.slug || button.name"
            :to="button.url"
            class="admin-grid-item"
            :title="button.name"
          >
            <div class="admin-button-tile">
              <div class="admin-button-icon" :class="button.class || button.colorClass || getAdminColorClass(index)">
                <ion-icon :name="button.icon || button.iconName || getAdminIconName(button.name)"></ion-icon>
              </div>
              <span class="admin-button-label">{{ button.name }}</span>
              <span class="admin-button-count" v-if="button.count">{{ button.count }}</span>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Bottom Spacing for Mobile Navigation -->
    <div class="bottom-spacing"></div>
  </div>

  <!-- Modals - Mobile Optimized -->
  <!-- Admin Level Switch Modal - Mobile Sheet -->
  <div class="modal fade action-sheet" id="adminSwitchModal" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('switch_admin_level') }}</h5>
        </div>
        <div class="modal-body">
          <div class="action-sheet-content">
            <button class="switch-option-btn" @click="changeAdminLevel">
              <div class="option-icon-circle">
                <ion-icon name="business"></ion-icon>
              </div>
              <div class="option-details">
                <span class="option-title">{{ currentAdminLevel == 7 ? admin_levels['org'] : (admin_levels['campus'] || 'Campus') }} {{ $t('admin')}}</span>
                <span class="option-desc">{{ $t('switch_to') }} {{ currentAdminLevel == 7 ? 'organization' : 'campus' }} {{ $t('level') }}</span>
              </div>
              <ion-icon name="arrow-forward-circle" class="option-action"></ion-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Self Registration Modal - Mobile Optimized -->
  <div class="modal fade action-sheet" id="addAttendanceModel" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content self-reg-modal-content">
        <div class="sheet-header sticky-header">
          <div class="sheet-drag-indicator"></div>
          <h5 class="sheet-title">
            <ion-icon name="person-add"></ion-icon>
            {{ $t('self_registration') }}
          </h5>

        </div>
        
        <div class="sheet-body">
          <div class="registration-notice">
            <ion-icon name="information-circle"></ion-icon>
            <p>{{ $t('registration_notice') }}</p>
          </div>
          
          <div class="mobile-members-list">
            <div class="mobile-member-item" v-for="member in members" :key="member.id">
              <label :for="'mobile_member_' + member.id" class="member-checkbox-label">
                <div class="member-left">
                  <input 
                    type="checkbox" 
                    :id="'mobile_member_' + member.id"
                    :value="member.id" 
                    v-model="members_attendance"
                    class="member-checkbox"
                  />
                  <div class="custom-check">
                    <ion-icon name="checkmark"></ion-icon>
                  </div>
                  <div class="member-details">
                    <span class="member-name">{{ member.name }}</span>
                    <span class="member-registered" v-if="member.event_reg == 1">
                      <ion-icon name="checkmark-circle"></ion-icon>
                      {{ $t('already_registered') }}
                    </span>
                  </div>
                </div>
                <div class="member-avatar">
                  {{ member.name.charAt(0).toUpperCase() }}
                </div>
              </label>
            </div>
          </div>
          
          <!-- Education Field -->
          <div class="education-section">
            <label class="education-label">വിദ്യാഭ്യാസ യോഗ്യത</label>
            <select class="education-select" v-model="selectedEducation">
              <option value="">--തിരഞ്ഞെടുക്കുക--</option>
              <option 
                v-for="level in educationLevels" 
                :key="level.id" 
                :value="level.id">
                {{ level.name_mal || level.name }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="sheet-footer sticky-footer">
          <div class="selected-count">
            <ion-icon name="people"></ion-icon>
            <span>{{ members_attendance.length }} {{ $t('selected') }}</span>
          </div>
          <div class="footer-buttons">

            <button 
              type="button" 
              class="mobile-btn-primary"
              :disabled="btn_loading || !members_attendance.length"
              @click="selfRegistration"
            >
              <span v-show="btn_loading" class="spinner-border spinner-border-sm"></span>
              <span v-show="!btn_loading">{{ $t('register') }} ({{ members_attendance.length }})</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Alert & Dialog Models -->
  <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
  <dialog-model 
    :message="dialog_message" 
    :action_method="dialog_action" 
    :action_method_params="dialog_action_params"
    ref="dialog_model"
  ></dialog-model>
</template>

<script>
import $ from "jquery";
import UserService from "../../services/user.service";
import AlertModel from "./../layouts/AlertModel.vue";
import DialogModel from "./../layouts/DialogModel.vue";

export default {
  name: "EventDashboard",
  props: {
    unit_id: null,
  },
  components: {
    AlertModel,
    DialogModel,
  },
  data() {
    return {
      alert_type: "",
      alert_message: "",
      dialog_message: "",
      dialog_action: "",
      dialog_action_params: [],
      event_id: '',
      members_attendance: [],
      loading: false,
      btn_loading: false,
      educationLevels: [],
      selectedEducation: null,
      event_details: {
        event: {
          banners: [],
          name: '',
          date: '',
          location: ''
        },
        admin_buttons: [],
        common_buttons: [],
        stats: {
          registered: 0,
          attended: 0,
          pending: 0
        }
      },
      permissions: [],
      admin_levels: [],
      currentAdminLevel: '',
      members: []
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    canSwitchAdminLevel() {
      // Any campus admin tier (1=state campus wing, 2=district campus wing, 3=individual
      // campus) who ALSO holds a real org role (user_level <= 4) can toggle between the two
      // views — each tier just needs its own scope identifier to actually be set.
      const level = this.currentUser.campus_user_level;
      if (!level || level < 1 || level > 3) {
        return false;
      }
      const hasCampusScope = level == 1
        || (level == 2 && this.currentUser.campus_district_id)
        || (level == 3 && this.currentUser.campus_id);
      return hasCampusScope &&
             this.currentUser.user_level <= 4 &&
             this.admin_levels['campus'];
    },
    mainAdminButtons() {
      const baseButtons = this.event_details.admin_buttons
        .filter(b => ![
          'Bus Details',
          'Check-in',
          'Parking',
          'Bus and Food Report For Confirmation',
        ].includes(b.name));
      
      // if (this.$route.params.slug === 'ktc') {
      //   return baseButtons.concat({
      //     name: 'രജിസ്റ്റർ ചെയ്യേണ്ട മെമ്പർമാർ',
      //     url: `/event/${this.$route.params.slug}/registrants-list/${this.event_id}`,
      //     icon: 'people-outline',  
      //     class: 'color-orange', 
      //     slug: 'registrants-list-custom'
      //   });
      // }
      
      return baseButtons;
    },
    busAdminButtons() {
      // Only bus-related buttons
      return this.event_details.admin_buttons.filter(b => [
        'Bus Details',
        'Check-in',
        'Parking',
        'Bus and Food Report For Confirmation'
      ].includes(b.name));
    }
  },
  mounted() {
    if (!this.currentUser) {
      location.replace("/");
    }
    this.event_id = this.$route.params.event_id;
    this.$root.updateParent(this.event_details.event.name);
    
    // A campus admin (any tier — state/district campus wing or individual campus) who has no
    // real org admin role (user_level > 4, i.e. a plain member) has nothing useful to do in
    // "org view", so default them straight into campus view.
    if (this.currentUser.campus_user_level && this.currentUser.user_level > 4) {
      localStorage.setItem('currentAdminLevel', 7);
    }
    this.currentAdminLevel = localStorage.getItem('currentAdminLevel');
    if (!this.currentAdminLevel) {
      localStorage.setItem('currentAdminLevel', this.currentUser.user_level);
    }
    this.currentAdminLevel = localStorage.getItem('currentAdminLevel');
    this.getEventDetails(this.currentAdminLevel);
    this.getEducationLevels();
    this.$root.updateParent(this.$t('event_dashboard'));
    
    window.onpopstate = function () {
      if ($("body").hasClass("modal-open")) {
        location.reload();
      }
    };
  },
  methods: {
    getEventDetails(admin_level = null) {
      this.loading = true;
      let data = {
        slug: this.$route.params.slug,
        page: 'event_dashboard',
        admin_level: admin_level,
        event_id: this.event_id
      };
      
      UserService.authPostRequest(`get-event-details`, data).then(
        (response) => {
          if (response.data.status == "success") {
            this.event_details = response.data.data.event_details;
            this.permissions = response.data.data.permissions;
            this.admin_levels = response.data.data.admin_levels;
            this.members = response.data.data.members;

            // Self-correct into campus view: the server is the source of truth for whether
            // this user is a campus admin (admin_levels.campus) — this can be more current
            // than a stale cached currentUser, e.g. someone just granted campus-admin access
            // who hasn't logged out/in since. If org view surfaced no admin actions at all
            // but campus capability exists, switch to campus view and refetch once.
            if (
              admin_level != 7 &&
              this.admin_levels && this.admin_levels.campus &&
              (!this.event_details.admin_buttons || this.event_details.admin_buttons.length === 0)
            ) {
              localStorage.setItem('currentAdminLevel', 7);
              this.currentAdminLevel = 7;
              this.getEventDetails(7);
              return;
            }

            this.members.forEach(element => {
              if (element.event_reg == 1) {
                this.members_attendance.push(element.id);
              }
            });
            this.loading = false;
          } else {
            this.loading = false;
          }
        },
        (error) => {
          this.loading = false;
          this.message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        }
      );
    },
    
    getIconName(buttonName) {
      const iconMap = {
        'Attendance': 'clipboard-outline',
        'Registration': 'person-add-outline',
        'Reports': 'document-text-outline',
        'Settings': 'settings-outline',
        'Volunteers': 'people-outline',
        'Schedule': 'calendar-outline',
        'Payments': 'card-outline',
        'Messages': 'chatbubbles-outline',
        'Donate a Contact': 'gift-outline',
        'Check-in': 'checkmark-circle-outline',
        'Teachers Directory': 'school-outline',
        'Bus Details': 'bus-outline',
        'Parking': 'car-outline',
        'Registration Summary': 'stats-chart-outline',
        'Bus and Food Report': 'restaurant-outline',
        'Bus & Food Confirmation': 'restaurant-outline'
      };
      return iconMap[buttonName] || 'grid-outline';
    },
    
    getAdminIconName(buttonName) {
      const iconMap = {
        'User Management': 'people-circle-outline',
        'Event Settings': 'cog-outline',
        'Analytics': 'analytics-outline',
        'Permissions': 'key-outline',
        'Audit Log': 'list-outline',
        'Export Data': 'download-outline',
        'Check-in': 'checkmark-circle-outline',
        'Registration': 'person-add-outline',
        'Registration Summary': 'stats-chart-outline',
        'Registration Summary Report': 'document-text-outline',
        'Teachers Directory': 'school-outline',
        'Bus Details': 'bus-outline',
        'Parking': 'car-outline',
        'Bus and Food Report For Confirmation': 'restaurant-outline',
        'Bus & Food Confirmation': 'restaurant-outline',
        'ഷെയർ ചെയ്യു കിട്ടിയത്': 'share-social-outline',
        'കഴിഞ്ഞ വർഷം ചെയ്യു കിട്ടിയത്': 'time-outline'
      };
      return iconMap[buttonName] || 'shield-checkmark-outline';
    },
    
    getButtonColorClass(index) {
      const colors = ['color-pink', 'color-blue', 'color-green', 'color-purple', 'color-orange', 'color-teal', 'color-indigo', 'color-red'];
      return colors[index % colors.length];
    },
    
    getAdminColorClass(index) {
      const colorMap = {
        0: 'color-pink',
        1: 'color-blue',
        2: 'color-green',
        3: 'color-blue-dark',
        4: 'color-gray',
        5: 'color-gray',
        6: 'color-pink',
        7: 'color-blue',
        8: 'color-green'
      };
      return colorMap[index] || 'color-gray';
    },
    
    showAdminSwitchModal() {
      $('#adminSwitchModal').modal('show');
    },
    
    changeAdminLevel() {
      if (this.currentAdminLevel == 7) {
        localStorage.setItem('currentAdminLevel', this.currentUser.user_level);
      } else {
        localStorage.setItem('currentAdminLevel', 7);
      }
      this.currentAdminLevel = localStorage.getItem('currentAdminLevel');
      $('#adminSwitchModal').modal('hide');
      this.getEventDetails(this.currentAdminLevel);
    },
    
    addAttendanceModelShow() {
      $('#addAttendanceModel').modal('show');
    },
    
    closeSelfModel() {
      $('#addAttendanceModel').modal('hide');
    },
    
    selfRegistration() {
      let multiple_members = null;
      let multiple_flag = false;
      
      if (this.members.length) {
        if (this.members_attendance.length) {
          multiple_members = this.members_attendance;
          multiple_flag = true;
        } else {
          this.alert_type = 'Warning';
          this.alert_message = 'പേരിന്റെ നേരെ ടിക്ക് ചെയ്തു രജിസ്റ്റർ ചെയ്യുക.';
          this.$refs.alert_model.openModel();
          return 0;
        }
      }
      
      this.btn_loading = true;
      let data = {
        multiple_members: multiple_members,
        multiple_flag: multiple_flag,
        event_type: this.$route.params.slug,
        event_id: this.event_id,
        daee_education: this.selectedEducation
      };
      
      UserService.authPostRequest('self-event-registration', data).then(
        (response) => {
          this.btn_loading = false;
          if (response.data.status == 'success') {
            $('#addAttendanceModel').modal('hide');
            if (!response.data.data.members) {
              this.alert_type = 'Success';
              this.alert_message = response.data.message;
              this.$refs.alert_model.openModel();
              this.members_attendance = [];
              this.getEventDetails(this.currentAdminLevel);
            } else {
              this.members = response.data.data.members;
              $('#addAttendanceModel').modal('show');
            }
          } else if (response.data.status == 'warning') {
            this.alert_type = 'Warning';
            this.alert_message = response.data.message;
            this.$refs.alert_model.openModel();
          } else {
            this.alert_type = 'Error';
            this.alert_message = response.data.message;
            this.$refs.alert_model.openModel();
          }
        },
        (error) => {
          this.btn_loading = false;
          this.alert_type = 'Error';
          this.alert_message = error.data.message;
          this.$refs.alert_model.openModel();
        }
      );
    },
    
    getEducationLevels() {
      let data = {
        code: "DAEE_EDUCATION",
        language: "ml"
      };
      
      UserService.authPostRequest('get-combobox', data).then(
        (response) => {
          if (response.data.status == "success") {
            if (response.data.data && response.data.data.list) {
              if (Array.isArray(response.data.data.list)) {
                this.educationLevels = response.data.data.list;
              } else if (response.data.data.list.options) {
                this.educationLevels = response.data.data.list.options;
              }
            }
          }
        },
        (error) => {
          console.log("Error fetching education levels", error);
        }
      );
    },
  },
};
</script>

<style scoped>
/* Mobile-First Base Styles */
* {
  -webkit-tap-highlight-color: transparent;
}

.mobile-optimized {
  min-height: 100vh;
  background: #f5f7fa;
}

/* Loading State */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}

/* Mobile Header Wrapper */
.mobile-header-wrapper {
  margin-bottom: 1rem;
}

/* Mobile Header with Banner */
.mobile-header {
  position: relative;
  margin: 1rem;
  border-radius: 16px;
  overflow: hidden;
}

.mobile-banner-slide {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.mobile-banner-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
}

.banner-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem 1rem 1rem;
}

.event-title-overlay {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  margin: 0;
}

/* Event Info Bar */
.event-info-bar {
  background: white;
  padding: 1.5rem 1rem;
  margin: 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.mobile-event-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a418c;
  margin: 0;
}

/* Event Meta Card */
.event-meta-card {
  background: white;
  margin: 0 1rem 1rem 1rem;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.meta-icon-wrapper {
  width: 36px;
  height: 36px;
  background: #f0f4f8;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.meta-icon-wrapper ion-icon {
  font-size: 1.25rem;
  color: #6c757d;
}

.meta-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.meta-label {
  font-size: 0.75rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.meta-value {
  font-size: 0.9375rem;
  color: #2c3e50;
  font-weight: 600;
}

.meta-divider {
  width: 1px;
  height: 30px;
  background: #e9ecef;
  margin: 0 1rem;
}

/* Mobile Primary CTA */
.mobile-primary-cta {
  padding: 0 1rem;
  margin-bottom: 1rem;
}

.cta-link {
  text-decoration: none;
}

.mobile-primary-btn {
  width: 100%;
  min-height: 48px;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 10px;
  background: #6366f1;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
  transition: all 0.3s ease;
  touch-action: manipulation;
  position: relative;
  overflow: hidden;
}

.mobile-primary-btn:active {
  transform: scale(0.98);
  background: #5558e3;
  box-shadow: 0 1px 4px rgba(99, 102, 241, 0.1);
}

.mobile-btn-content {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.btn-icon-wrapper {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-icon-wrapper ion-icon {
  font-size: 1.125rem;
}

.btn-text-wrapper {
  flex: 1;
  text-align: left;
}

.btn-main-text {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.0625rem;
  line-height: 1.2;
}

.btn-sub-text {
  display: block;
  font-size: 0.75rem;
  opacity: 0.9;
  line-height: 1.2;
}

.btn-chevron {
  font-size: 1.125rem;
  opacity: 0.7;
}

/* Mobile Stats Bar */
.mobile-stats-bar {
  background: white;
  padding: 1rem;
  margin: 0 1rem 1rem 1rem;
  border-radius: 12px;
  display: flex;
  justify-content: space-around;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a418c;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-divider {
  width: 1px;
  background: #e9ecef;
  margin: 0 0.5rem;
}

/* Mobile Quick Actions */
.mobile-actions-section {
  margin-bottom: 1.5rem;
}

.section-content-wrapper {
  padding: 0 1rem;
}

.section-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.mobile-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.action-count {
  background: #e9ecef;
  color: #6c757d;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.mobile-action-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.mobile-action-item {
  text-decoration: none;
}

.action-tile {
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  padding: 0.75rem 0.5rem;
  min-height: 95px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  position: relative;
  touch-action: manipulation;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.action-tile:active {
  transform: scale(0.97);
  background: #f8f9fa;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
}

.tile-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.tile-icon ion-icon {
  font-size: 1.125rem;
  color: white;
}

.tile-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
  text-align: center;
  word-wrap: break-word;
  max-width: 100%;
}

.tile-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ef4444;
  color: white;
  font-size: 0.5625rem;
  font-weight: 700;
  padding: 0.125rem 0.25rem;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.section-wrapper {
  padding: 1rem;
}

.admin-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.admin-header-icon {
  color: #4169E1;
  font-size: 1.25rem;
}

.admin-state-badge {
  background: #e8f4fd;
  color: #1e40af;
  padding: 0.3rem 0.75rem;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  border: 1px solid #bfdbfe;
  align-self: flex-start;
  max-width: 100%;
  white-space: normal;
  line-height: 1.35;
}

.admin-level-switch {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem;
  background: #f8f9fa;
  border: 1px dashed #cbd5e0;
  border-radius: 10px;
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
}

.admin-level-switch:active {
  background: #e9ecef;
  transform: scale(0.98);
  border-color: #4169E1;
  color: #4169E1;
}

.admin-level-switch ion-icon {
  font-size: 1rem;
}

.admin-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.admin-grid-item {
  text-decoration: none;
  display: block;
}
/* Add spacing for bus-section */
.bus-section {
  margin-top: 2rem;
}

/* Button-style admin tiles */
.admin-button-tile {
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 14px;
  padding: 1rem 0.75rem;
  width: 100%;
  min-height: 120px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 120px;
  min-height: 120px;
  max-height: 120px;
  overflow: hidden;
  text-align: center;
  /* Let icon and label share space, icon shrinks if label is long */
  > .admin-button-icon {
    flex-shrink: 2;
    transition: width 0.2s, height 0.2s;
  }
  > .admin-button-label {
    flex-shrink: 1;
    font-size: clamp(0.7rem, 1.2vw, 0.95rem);
    line-height: 1.1;
    word-break: break-word;
    white-space: normal;
    overflow-wrap: break-word;
    max-height: 100%;
    display: block;
  }
  /* Allow icon and label to shrink if needed */
  height: 120px;
  min-height: 120px;
  max-height: 120px;
  overflow: hidden;
  text-align: center;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
  border: 1.5px solid #e5e7eb;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.admin-button-tile:hover {
  background: linear-gradient(145deg, #f8f9fa, #f3f4f6);
  transform: translateY(-2px);
  box-shadow: 
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border-color: #d1d5db;
}

.admin-button-tile:active {
  transform: scale(0.98) translateY(0);
  background: linear-gradient(145deg, #f3f4f6, #e5e7eb);
  box-shadow: 
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06),
    inset 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* Circular icon with shadow */
.admin-button-icon {
  width: clamp(32px, 5vw, 50px);
  height: clamp(32px, 5vw, 50px);
  min-width: 40px;
  min-height: 40px;
  max-width: 50px;
  max-height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background-image: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%);
  flex-shrink: 1;
  transition: width 0.2s, height 0.2s;
}

.admin-button-icon ion-icon {
  font-size: 1.375rem;
  color: white;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

/* Button label */
.admin-button-label {
  font-size: clamp(0.7rem, 1.5vw, 0.95rem);
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
  text-align: center;
  word-break: break-word;
  padding: 0 0.25rem;
  width: 100%;
  white-space: normal;
  overflow-wrap: break-word;
  max-height: 100%;
  display: block;
}

/* Badge count */
.admin-button-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.25rem 0.375rem;
  border-radius: 12px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 
    0 4px 6px -1px rgba(239, 68, 68, 0.3),
    0 2px 4px -1px rgba(239, 68, 68, 0.2);
  z-index: 2;
}

/* Color Classes with Gradients */
.color-pink {
  background: linear-gradient(135deg, #EC4899, #DB2777);
}

.color-blue {
  background: linear-gradient(135deg, #3B82F6, #2563EB);
}

.color-blue-dark {
  background: linear-gradient(135deg, #2563EB, #1D4ED8);
}

.color-green {
  background: linear-gradient(135deg, #10B981, #059669);
}

.color-purple {
  background: linear-gradient(135deg, #8B5CF6, #7C3AED);
}

.color-orange {
  background: linear-gradient(135deg, #F59E0B, #D97706);
}

.color-teal {
  background: linear-gradient(135deg, #14B8A6, #0D9488);
}

.color-gray {
  background: linear-gradient(135deg, #6B7280, #4B5563);
}

.color-indigo {
  background: linear-gradient(135deg, #6366F1, #4F46E5);
}

.color-red {
  background: linear-gradient(135deg, #EF4444, #DC2626);
}


/* Component-specific modal content styles only */

/* Registration Modal */
.registration-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #e3f2fd;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.registration-notice ion-icon {
  font-size: 1.25rem;
  color: #1a418c;
  flex-shrink: 0;
}

.registration-notice p {
  margin: 0;
  font-size: 0.875rem;
  color: #495057;
  line-height: 1.5;
}

.mobile-members-list {
  padding: 0.5rem 0;
}

.mobile-member-item {
  margin-bottom: 0.75rem;
}

.member-checkbox-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.member-checkbox-label:active {
  background: #f8f9fa;
  transform: scale(0.98);
}

.member-checkbox {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.member-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.custom-check {
  width: 24px;
  height: 24px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.member-checkbox:checked ~ .custom-check {
  background: #1a418c;
  border-color: #1a418c;
}

.custom-check ion-icon {
  color: white;
  font-size: 1rem;
  opacity: 0;
  transform: scale(0);
  transition: all 0.2s ease;
}

.member-checkbox:checked ~ .custom-check ion-icon {
  opacity: 1;
  transform: scale(1);
}

.member-details {
  flex: 1;
}

.member-name {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.125rem;
}

.member-registered {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #28a745;
}

.member-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
}

/* Footer Buttons */
.sticky-footer {
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 10;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.75rem;
}

.footer-buttons {
  display: flex;
  gap: 0.75rem;
}

.mobile-btn-secondary,
.mobile-btn-primary {
  flex: 1;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: all 0.2s ease;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mobile-btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
}

.mobile-btn-primary {
  background: #1a418c;
  color: white;
}

/* Bottom Spacing */
.bottom-spacing {
  height: 80px;
}

/* Responsive */
@media (max-width: 360px) {
  .mobile-action-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .admin-actions-grid {
    gap: 0.5rem;
  }
  
  .admin-button-tile {
  min-height: 120px;
  height: 120px;
  padding: 0.875rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  }
  
  .admin-button-label {
    font-size: 0.75rem;
  }
}

@media (min-width: 576px) {
  .mobile-action-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .admin-actions-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) {
  .mobile-optimized {
    margin: 0 auto;
  }
  
  .admin-actions-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Hover Effects */
@media (hover: hover) {
  .action-tile:hover,
  .admin-button-tile:hover {
    transform: translateY(-2px);
  }
}

/* Touch Feedback */
button:active,
a:active {
  opacity: 0.8;
}

/* Prevent Text Selection */
button,
.action-tile,
.admin-button-tile {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Smooth Scrolling */
.sheet-body {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* Hide Scrollbars */
.sheet-body::-webkit-scrollbar {
  display: none;
}

/* Self Registration modal: sticky header + scrollable middle + sticky footer, all capped so
   the modal never grows past the viewport and hides behind the bottom nav. Sits inside the
   global .modal.action-sheet wrapper, which already handles fixed bottom-sheet positioning
   and z-index above the footer nav. */
.self-reg-modal-content {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  max-height: 85dvh;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.self-reg-modal-content .sheet-header {
  flex-shrink: 0;
  padding: 16px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.self-reg-modal-content .sheet-drag-indicator {
  width: 40px;
  height: 4px;
  background: #d0d0d0;
  border-radius: 2px;
  margin: 0 auto 10px;
}

.self-reg-modal-content .sheet-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #27173E;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.self-reg-modal-content .sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.self-reg-modal-content .sheet-footer {
  flex-shrink: 0;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #f0f0f0;
  background: #fff;
}

/* Admin/campus switch option row */
.switch-option-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  text-align: left;
}

.option-icon-circle {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eef2ff;
  color: #1a418c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.option-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-title {
  font-size: 14px;
  font-weight: 700;
  color: #27173E;
  word-break: break-word;
}

.option-desc {
  font-size: 12px;
  color: #8a94a6;
}

.option-action {
  flex-shrink: 0;
  font-size: 22px;
  color: #adb5bd;
}

/* Focus States */
button:focus,
a:focus {
  outline: 2px solid #1a418c;
  outline-offset: 2px;
}

/* PWA Safe Area Support */
@supports (padding: max(0px)) {
  .mobile-optimized {
    padding-left: max(0px, env(safe-area-inset-left));
    padding-right: max(0px, env(safe-area-inset-right));
  }
  
  .bottom-spacing {
    height: calc(20px + env(safe-area-inset-bottom));
  }
}

/* Education Field Section */
.education-section {
  padding: 0.75rem 1rem 0;
  background: white;
}

.education-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.education-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: white;
  color: #2c3e50;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236c757d' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
}

.education-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}
</style>